// Shrinks a photo/graphic in the browser before it is uploaded, so a 12 MB PNG
// doesn't end up as the original that every page has to load.
//
// - Scenepack / poster images are capped at 2000 px on the long side, avatars at 512 px.
// - The result is a WebP (transparency is kept).
// - GIFs (animated avatars), SVGs and anything the browser can't decode are left alone,
//   and if the smaller version somehow isn't smaller, the original is uploaded.

const MAX_SIDE = { image: 2000, avatar: 512 };
const LEAVE_ALONE_UNDER = 350 * 1024; // already small and within the size cap

export async function compressImageForUpload(file, kind = "image") {
  try {
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) return file;
    const bitmap = await createImageBitmap(file);
    const max = MAX_SIDE[kind] || MAX_SIDE.image;
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < LEAVE_ALONE_UNDER) {
      bitmap.close?.();
      return file;
    }
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", 0.86));
    if (!blob || blob.type !== "image/webp" || (blob.size >= file.size && scale === 1)) return file;
    const name = (file.name || "image").replace(/\.[^.]+$/, "") + ".webp";
    return new File([blob], name, { type: "image/webp" });
  } catch {
    return file; // never block an upload because shrinking failed
  }
}
